/**
 * FE-23: Global Filters — Platform / Country / Language dropdowns.
 * Filters are synced to URL query params so metrics tabs can read them.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import { t } from "../i18n";

// ─── Types ───

const PLATFORM_OPTIONS = ["all", "ios", "android", "web", "desktop"] as const;

type GeoOption = {
  readonly code: string;
  readonly label: string;
};

type LocaleOption = {
  readonly code: string;
  readonly label: string;
};

export type GlobalFilterValues = {
  readonly platform: string;
  readonly country: string;
  readonly locale: string;
};

// ─── Main Component ───

export function GlobalFilters(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries, locales } = useFilterOptions();

  const platform = searchParams.get("platform") ?? "all";
  const country = searchParams.get("country") ?? "all";
  const locale = searchParams.get("locale") ?? "all";

  const updateParam = useCallback(
    (key: string, value: string): void => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <div style={s.row}>
      <FilterSelect
        label={t("filters.platform")}
        value={platform}
        onChange={(v) => updateParam("platform", v)}
        options={PLATFORM_OPTIONS.map((p) => ({
          value: p,
          label: p === "all" ? t("filters.all_platforms") : capitalize(p),
        }))}
      />
      <FilterSelect
        label={t("filters.country")}
        value={country}
        onChange={(v) => updateParam("country", v)}
        options={[
          { value: "all", label: t("filters.all_countries") },
          ...countries.map((c) => ({ value: c.code, label: c.label })),
        ]}
      />
      <FilterSelect
        label={t("filters.language")}
        value={locale}
        onChange={(v) => updateParam("locale", v)}
        options={[
          { value: "all", label: t("filters.all_languages") },
          ...locales.map((l) => ({ value: l.code, label: l.label })),
        ]}
      />
    </div>
  );
}

// ─── Filter Select ───

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}): React.JSX.Element {
  return (
    <label style={s.label}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={s.select}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── Hook: read global filters from URL ───

export function useGlobalFilters(): GlobalFilterValues {
  const [searchParams] = useSearchParams();
  return {
    platform: searchParams.get("platform") ?? "all",
    country: searchParams.get("country") ?? "all",
    locale: searchParams.get("locale") ?? "all",
  };
}

// ─── Hook: fetch dropdown options ───

function useFilterOptions(): {
  countries: ReadonlyArray<GeoOption>;
  locales: ReadonlyArray<LocaleOption>;
} {
  const [countries, setCountries] = useState<GeoOption[]>([]);
  const [locales, setLocales] = useState<LocaleOption[]>([]);

  useEffect(() => {
    void loadOptions(setCountries, setLocales);
  }, []);

  return { countries, locales };
}

async function loadOptions(
  setCountries: (v: GeoOption[]) => void,
  setLocales: (v: LocaleOption[]) => void,
): Promise<void> {
  try {
    const [geoRes, localeRes] = await Promise.all([
      apiFetch<{ data: { items: GeoOption[] } }>("/admin/metrics/geo"),
      apiFetch<{ data: { items: LocaleOption[] } }>("/admin/metrics/locale"),
    ]);
    setCountries(geoRes.data.items);
    setLocales(localeRes.data.items);
  } catch {
    // Silently fall back to empty options
  }
}

// ─── Helpers ───

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  row: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 20,
    padding: "12px 0",
    borderBottom: "1px solid var(--color-border)",
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  select: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    background: "var(--color-bg)",
    color: "var(--color-text)",
    minWidth: 140,
  },
};
